import { type JSX, useMemo } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Link } from 'Components/Buttons/Buttons';
import { QuestionMultipleSelect } from 'Components/DueDiligenceQuestions/QuestionMultipleSelect';
import { QuestionSingleSelect } from 'Components/DueDiligenceQuestions/QuestionSingleSelect';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { FormLabel } from 'Components/FormField/FormLabel/FormLabel';
import { RequiresAttentionMarker } from 'Components/RequiresAttentionMarker/RequiresAttentionMarker';
import { Text } from 'Components/Text/Text';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getIssueDetailsUrl } from 'Helpers/URLBuilder/URLBuilder';
import { DueDiligenceQuestion, DueDiligenceQuestionType, hasUnacceptableAnswer, isQuestionAnswered } from 'Models/DueDiligence';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import styles from './QuestionView.module.css';

export interface QuestionViewProps {
    documentApi: DocumentApi;
    issueIdToTitleMap: Map<string, string>;
    module: DueDiligenceModule;
    question: DueDiligenceQuestion;

    controlLastSavedAt?: string;
}

/** Shows the read-only version of a question to a SummIT user for the purpose of assessing the control to which the question belongs. */
export const QuestionView = (props: QuestionViewProps): JSX.Element => {
    const questionId = `question-${props.question.id}`;
    const isAnswered = isQuestionAnswered(props.question);
    const isRequired = props.question.is_required;
    const isUnacceptableAnswer = hasUnacceptableAnswer(props.question);

    const generatedIssueId = useMemo(() => {
        if (props.question._type === DueDiligenceQuestionType.SINGLE_SELECT || props.question._type === DueDiligenceQuestionType.MULTIPLE_SELECT) {
            return props.question.generated_issue_id;
        }
        return undefined;
    }, [props.question]);

    const issueTitle = useMemo(() => {
        return generatedIssueId ? props.issueIdToTitleMap.get(generatedIssueId) : undefined;
    }, [generatedIssueId, props.issueIdToTitleMap]);

    const questionWasUpdatedSinceControlLastSaved = useMemo(() => {
        if (!props.question.last_updated_at || !props.controlLastSavedAt) {
            return false;
        }
        return props.question.last_updated_at > props.controlLastSavedAt;
    }, [props.question.last_updated_at, props.controlLastSavedAt]);

    const renderQuestionContent = (): JSX.Element => {
        switch (props.question._type) {
            case DueDiligenceQuestionType.DATE:
                return (
                    <>
                        <div>
                            <FormLabel>Answer</FormLabel>
                            {/* The "unable to answer" check must come first: such a question counts as answered but has no date to render. */}
                            {props.question.is_unable_to_answer ? (
                                <Text variant="Text3" color="darkGray">
                                    {`The ${props.module === ProductModule.UCRM ? 'respondent' : 'third party'} indicated that they are unable to answer this question.`}
                                </Text>
                            ) : isAnswered ? (
                                <Text>{iso8601ToUsDateShort(props.question.answer!)}</Text>
                            ) : (
                                <Text variant="Text3" color="darkGray">
                                    Not answered
                                </Text>
                            )}
                        </div>

                        {props.question.files.length > 0 && (
                            <div>
                                <FormLabel>Files</FormLabel>
                                <div className={styles.fileList}>
                                    {props.question.files.map((file) => (
                                        <UploadedFileAndState key={file.file_id} documentApi={props.documentApi} file={file} onDownloadError={(errorMessage) => console.error(errorMessage)} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {props.question.comments && (
                            <div>
                                <FormLabel>Comments</FormLabel>
                                <Text preserveLineBreaks>{props.question.comments}</Text>
                            </div>
                        )}
                    </>
                );

            case DueDiligenceQuestionType.FILE_UPLOAD: {
                return (
                    <>
                        <div>
                            <FormLabel>Answer</FormLabel>
                            {/* The "unable to answer" check must come first: such a question counts as answered but has no files to render. */}
                            {props.question.is_unable_to_answer ? (
                                <Text variant="Text3" color="darkGray">
                                    {`The ${props.module === ProductModule.UCRM ? 'respondent' : 'third party'} indicated that they are unable to answer this question.`}
                                </Text>
                            ) : isAnswered ? (
                                <div className={styles.fileList}>
                                    {props.question.files.map((file) => (
                                        <UploadedFileAndState key={file.file_id} documentApi={props.documentApi} file={file} onDownloadError={(errorMessage) => console.error(errorMessage)} />
                                    ))}
                                </div>
                            ) : (
                                <Text variant="Text3" color="darkGray">
                                    Not answered
                                </Text>
                            )}
                        </div>

                        {props.question.comments && (
                            <div>
                                <FormLabel>Comments</FormLabel>
                                <Text preserveLineBreaks>{props.question.comments}</Text>
                            </div>
                        )}
                    </>
                );
            }

            case DueDiligenceQuestionType.FREEFORM_TEXT:
                return (
                    <>
                        <div>
                            <FormLabel>Answer</FormLabel>
                            {isAnswered ? (
                                <Text preserveLineBreaks>{props.question.comments}</Text>
                            ) : (
                                <Text variant="Text3" color="darkGray">
                                    Not answered
                                </Text>
                            )}
                        </div>

                        {props.question.files.length > 0 && (
                            <div>
                                <FormLabel>Files</FormLabel>
                                <div className={styles.fileList}>
                                    {props.question.files.map((file) => (
                                        <UploadedFileAndState key={file.file_id} documentApi={props.documentApi} file={file} onDownloadError={(errorMessage) => console.error(errorMessage)} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                );

            case DueDiligenceQuestionType.MULTIPLE_SELECT:
                return (
                    <>
                        <QuestionMultipleSelect answerOptions={props.question.options} formFieldId={questionId} defaultSelectedIndices={props.question.answer} onAnswerChanged={() => void 0} disabled={true} />

                        {props.question.files.length > 0 && (
                            <div>
                                <FormLabel>Files</FormLabel>
                                <div className={styles.fileList}>
                                    {props.question.files.map((file) => (
                                        <UploadedFileAndState key={file.file_id} documentApi={props.documentApi} file={file} onDownloadError={(errorMessage) => console.error(errorMessage)} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {props.question.comments && (
                            <div>
                                <FormLabel>Comments</FormLabel>
                                <Text preserveLineBreaks>{props.question.comments}</Text>
                            </div>
                        )}
                    </>
                );

            case DueDiligenceQuestionType.SINGLE_SELECT:
                return (
                    <>
                        <QuestionSingleSelect answerOptions={props.question.options} formFieldLabelId={questionId} defaultSelectedIndex={props.question.answer} onAnswerChanged={() => void 0} disabled={true} />

                        {props.question.files.length > 0 && (
                            <div>
                                <FormLabel>Files</FormLabel>
                                <div className={styles.fileList}>
                                    {props.question.files.map((file) => (
                                        <UploadedFileAndState key={file.file_id} documentApi={props.documentApi} file={file} onDownloadError={(errorMessage) => console.error(errorMessage)} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {props.question.comments && (
                            <div>
                                <FormLabel>Comments</FormLabel>
                                <Text preserveLineBreaks>{props.question.comments}</Text>
                            </div>
                        )}
                    </>
                );
        }
    };

    return (
        <>
            <Text variant="Text2" noStyles>
                {props.question.text}
            </Text>
            {(!isAnswered || isUnacceptableAnswer || questionWasUpdatedSinceControlLastSaved) && (
                <div className={styles.questionMarkers}>
                    {!isAnswered && (
                        <RequiresAttentionMarker type={isRequired ? 'negative' : 'warning'} size="sm">
                            {`Not answered${isRequired ? ' (required)' : ''}`}
                        </RequiresAttentionMarker>
                    )}
                    {isUnacceptableAnswer && (
                        <>
                            <RequiresAttentionMarker type="negative" size="sm">
                                Unacceptable answer
                            </RequiresAttentionMarker>
                            {generatedIssueId && issueTitle && (
                                <div className={styles.autoGeneratedIssueContainer}>
                                    <Text noStyles>Auto-generated issue:</Text>
                                    <Link variant="link" size="sm" to={getIssueDetailsUrl(generatedIssueId, props.module)} openInNewTab>
                                        {issueTitle}
                                    </Link>
                                </div>
                            )}
                        </>
                    )}
                    {questionWasUpdatedSinceControlLastSaved && (
                        <RequiresAttentionMarker type="warning" size="sm">
                            Updated since last assessment
                        </RequiresAttentionMarker>
                    )}
                </div>
            )}
            {renderQuestionContent()}
        </>
    );
};
